import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    OnDestroy,
    inject,
    ModelSignal,
    model,
    ViewEncapsulation,
    Signal,
    computed,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription, SubscriptionPricingPackage, UsersService } from "../users.service";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";
import { SubscriptionStatus } from "../../../../../shared/_types/subscription-status.type";
import { PricingPackage, PricingPackagesService, PricingPackageType } from "../../_services/pricing-packages.service";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-subscription-edit-dialog",
    templateUrl: "./subscription-edit-dialog.component.html",
    styleUrls: ["./subscription-edit-dialog.component.scss"],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class SubscriptionEditDialogComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();

    readonly data = inject<any>(MAT_DIALOG_DATA);
    readonly userId: ModelSignal<string | null> = model<string | null>(this.data.userId);
    readonly subscription: ModelSignal<Subscription | null> = model<Subscription | null>(null);
    readonly pricingPackages: ModelSignal<PricingPackage[]> = model<PricingPackage[]>([]);
    readonly isComplete: Signal<boolean> = computed(() => {
        const subscription = this.subscription();
        return (
            subscription !== null &&
            subscription.pricingPackages.length > 0 &&
            subscription.pricingPackages.filter((pkg) => pkg.isMain).length === 1 &&
            subscription.pricingPackages.every((pkg) => pkg.pricingPackageId)
        );
    });
    readonly displayedColumns: string[] = ["packageName", "isMain", "expirationDate", "actions", "idx"];

    get subscriptionStatusTypes(): number[] {
        return Object.values(SubscriptionStatus).filter((value) => typeof value === "number") as number[];
    }

    constructor(
        private userService: UsersService,
        private notificationService: NotificationsService,
        private pricingPackagesService: PricingPackagesService,
        public dialogRef: MatDialogRef<SubscriptionEditDialogComponent>,
    ) {}

    ngOnInit(): void {
        this.userService
            .getSubscription(this.userId())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((subscription) => {
                this.subscription.set(subscription);
            });

        this.pricingPackagesService
            .list()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((pricingPackages) => {
                this.pricingPackages.set(pricingPackages);
            });
    }

    update(): void {
        this.userService
            .updateSubscription(this.userId()!, this.subscription()!)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.notificationService.showSuccess("Success", "Subscription updated successfully");
                this.dialogRef.close(true);
            });
    }

    onIsMainUpdate(element: SubscriptionPricingPackage): void {
        element.isMain = !element.isMain;

        const pricingPackages = this.subscription()!.pricingPackages!;
        for (let i = 0; i < pricingPackages.length; i++) {
            if (pricingPackages[i] === element) {
                continue;
            }

            pricingPackages[i].isMain = false;
        }
        this.subscription.update((cur) => ({ ...cur!, pricingPackages: pricingPackages }));
    }

    delete(element: SubscriptionPricingPackage): void {
        const pricingPackages = this.subscription()!.pricingPackages!;
        this.subscription.update((cur) => ({
            ...cur!,
            pricingPackages: pricingPackages.filter((pkg) => pkg !== element),
        }));
    }

    addPackage(): void {
        const pricingPackages = this.subscription()!.pricingPackages!;
        const newPackage: SubscriptionPricingPackage = {
            subscriptionId: this.subscription()!.id,
            pricingPackageId: "",
            expirationDate: null,
            isMain: false,
        };
        this.subscription.update((cur) => ({
            ...cur!,
            pricingPackages: [...pricingPackages, newPackage],
        }));
    }

    isAddonPackage(pricingPackageId: string): boolean {
        if (!pricingPackageId) {
            return true;
        }
        const pricingPackage: PricingPackage | undefined = this.pricingPackages().find(
            (pkg) => pkg.id === pricingPackageId,
        );
        if (!pricingPackage) {
            return true;
        }
        const packageType: PricingPackageType = pricingPackage.type;
        return packageType === PricingPackageType.OneTimeAddon || packageType === PricingPackageType.SubscriptionAddon;
    }

    onSubscriptionUpdated(): void {
        this.subscription.update((cur) => ({ ...cur! }));
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
